import { SnackbarProvider, useSnackbar } from 'notistack';
import { IconButton, makeStyles, useMediaQuery, useTheme, Icons } from '~/lib/bn-material-ui';
import React, { useEffect, useRef } from 'react';

const useStyles = makeStyles(({ palette }) => ({
  root: {
    '& button': { // actions
      color: 'white',
    },
    '& $warning button': {
      color: palette.black,
    },
    '& div[role=alert]': { // 'default' alerts don't have a nice handle or class, so I'm using this obscure selector.
      backgroundColor: palette.gray[70],
    },
    borderRadius: '5px',
  },
  info: {
    backgroundColor: `${palette.indigo[50]} !important`,
  },
  warning: {
    backgroundColor: `${palette.yellow[20]} !important`,
    color: `${palette.black} !important`,
  },
  error: {
    backgroundColor: `${palette.red[20]} !important`,
  },
  success: {
    backgroundColor: `${palette.green[20]} !important`,
  },
}));

let isMounted = true;

/**
 * Don't use `useSnackbar` directly.
 * The Snackbar is only mounted when using the ui-customers component.  For now we just ignore messages that come
 * in when the snackbar is not mounted.
 */
export function useBnSnackbar() {
  const snackbar = useSnackbar();
  return {
    enqueueSnackbar: (...args) => {
      if (isMounted) {
        return snackbar?.enqueueSnackbar?.(...args);
      }
    },
    closeSnackbar: (...args) => {
      if (isMounted) {
        return snackbar?.closeSnackbar?.(...args);
      }
    },
  };
}

export default function BnSnackbarProvider({ children }) {
  const styles = useStyles();
  const snackbarRef = useRef();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  useEffect(() => {
    isMounted = true;

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <SnackbarProvider
      classes={{
        root: styles.root,
        contentRoot: styles.styled,
        variantSuccess: styles.success,
        variantInfo: styles.info,
        variantWarning: styles.warning,
        variantError: styles.error,
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      ref={snackbarRef}
      maxSnack={isMobile ? 1 : 5}
      action={(id) => {
        return (<IconButton onClick={() => { snackbarRef.current.closeSnackbar(id); }} size='large'><Icons.Clear /></IconButton>);
      }}>
      {children}
    </SnackbarProvider>
  );
}
